import React, { type FC, useMemo } from "react";
import notification from "@app/services/notification";
import confirm from "@app/services/confirm/confirm";
import { BusinessForm } from "@src/components/business-form";
import { BusinessSelect } from "@src/components/business-data-entry";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel, type FormPanelRenderProps } from "@shared/ui/form-panel";
import { EmptyState } from "@shared/ui/empty-state";
import { DetailList } from "@shared/ui/detail-row";
import { PickerOptionCard } from "@shared/ui/picker-option";
import { useWindowControls } from "@shared/ui/overlay/hooks/useWindowControls";
import { measureEndDecorator } from "@src/helpers/business/measureEndDecorator";
import { useQueryData } from "@src/hooks/useQueryData";
import type { StorePlaceRemainder } from "../../../../../../api/storePlaces";
import {
    stockKanbanContainersServices,
    type StockKanbanContainer,
    type StockKanbanContainerContent,
} from "../../../../../../api/stockKanbanContainers";
import { formatStorePlaceCellName } from "../../../../helpers/formatStorePlaceCellName";
import type { UseStockKanbanSupplyCardMutationsResult } from "../../useStockKanbanSupplyCardMutations";
import { useStockKanbanSupplyContentsQuery } from "../../hooks";
import {
    loadStockKanbanSupplyAvailableLotByCode,
    type StockKanbanSupplyAvailableLotOption,
} from "../../helpers/loadStockKanbanSupplyLotAvailability";
import { resolveStockKanbanSupplyContentMaxQty } from "../../helpers/resolveStockKanbanSupplyContentAvailableQty";
import { resolveStockKanbanSupplyLotPlacementMode } from "../../helpers/resolveStockKanbanSupplyLotPlacementMode";
import {
    StockKanbanSupplyAvailableLotSearchSelect,
    stockKanbanSupplyAvailableLotByIdQueryKey,
} from "../StockKanbanSupplyAvailableLotSearchSelect";
import {
    stockKanbanSupplyContentFormConfig,
    type StockKanbanSupplyContentFormContext,
    type StockKanbanSupplyContentFormValues,
} from "./stockKanbanSupplyContent.form-config";

/** Пропсы компонента {@link StockKanbanSupplyContentForm}. */
export interface StockKanbanSupplyContentFormProps {
    /** Код контейнера/объекта системы. */
    containerCode: number;
    /** Контейнер КАНБАН, для которого выбирается доступная партия. */
    container: StockKanbanContainer;
    /** Код редактируемой строки партии; `undefined` — создание. */
    contentCode?: number;
    /** Начальная партия, найденная сканированием. */
    initialLotCode?: number | null;
    /** Начальное количество, рассчитанное по остатку к комплектованию. */
    initialQty?: number;
    /**
     * Код ячейки размещения, найденный при скане. \
     * `null` — нужно выбрать вручную (multi); `0` — «на полу»; `undefined` — ручной ввод без скана.
     */
    initialSpCode?: number | null;
    /**
     * Режим только просмотра. \
     * Статус ≠ «на комплектовании» или текущий пользователь не комплектовщик.
     */
    readonly?: boolean;
    /** Мутация создания партии. */
    createContentMutation: UseStockKanbanSupplyCardMutationsResult["createContentMutation"];
    /** Мутация обновления партии. */
    updateContentMutation: UseStockKanbanSupplyCardMutationsResult["updateContentMutation"];
    /** Мутация удаления партии. */
    removeContentMutation: UseStockKanbanSupplyCardMutationsResult["removeContentMutation"];
}

/** Форма создания/обновления выбранной партии контейнера КАНБАН. */
export const StockKanbanSupplyContentForm: FC<StockKanbanSupplyContentFormProps> = ({
    containerCode,
    container,
    contentCode,
    initialLotCode = null,
    initialQty = 0,
    initialSpCode,
    readonly = false,
    createContentMutation,
    updateContentMutation,
    removeContentMutation,
}) => {
    const contentsQuery = useStockKanbanSupplyContentsQuery(containerCode);
    const content = contentCode != null ? contentsQuery.content.find((item) => item.Code === contentCode) : undefined;

    const containerCapacityMax = useMemo(() => {
        const occupiedExcludingCurrent = contentsQuery.content.reduce((sum, item) => {
            if (content != null && item.Code === content.Code) {
                return sum;
            }
            return sum + item.Qty;
        }, 0);

        return Math.max(container.Qty - occupiedExcludingCurrent, 0);
    }, [container.Qty, content, contentsQuery.content]);

    // Партия известна заранее (edit или скан): резолвим обогащённую опцию до монтирования формы.
    const knownLotCode = content?.LotCode ?? initialLotCode;
    const initialLotQuery = useQueryData({
        queryKey: stockKanbanSupplyAvailableLotByIdQueryKey(container, knownLotCode),
        queryFn: async ({ signal }) => {
            if (knownLotCode == null) {
                return null;
            }
            return loadStockKanbanSupplyAvailableLotByCode(container, knownLotCode, { signal });
        },
        enabled: knownLotCode != null,
    });

    if (contentCode != null && content == null && !contentsQuery.isFetching) {
        return <EmptyState text="Партия контейнера не найдена" />;
    }

    // Ждём резолв известной партии, чтобы форма не мигала пустым полем.
    if (knownLotCode != null && initialLotQuery.data == null && initialLotQuery.isFetching) {
        return null;
    }

    return (
        <StockKanbanSupplyContentFormBody
            containerCode={containerCode}
            container={container}
            content={content}
            containerCapacityMax={containerCapacityMax}
            initialLot={initialLotQuery.data ?? null}
            initialQty={initialQty}
            initialSpCode={initialSpCode}
            readonly={readonly}
            createContentMutation={createContentMutation}
            updateContentMutation={updateContentMutation}
            removeContentMutation={removeContentMutation}
        />
    );
};

interface StockKanbanSupplyContentFormBodyProps {
    containerCode: number;
    container: StockKanbanContainer;
    content?: StockKanbanContainerContent;
    containerCapacityMax: number;
    initialLot: StockKanbanSupplyAvailableLotOption | null;
    initialQty: number;
    initialSpCode?: number | null;
    readonly: boolean;
    createContentMutation: UseStockKanbanSupplyCardMutationsResult["createContentMutation"];
    updateContentMutation: UseStockKanbanSupplyCardMutationsResult["updateContentMutation"];
    removeContentMutation: UseStockKanbanSupplyCardMutationsResult["removeContentMutation"];
}

const StockKanbanSupplyContentFormBody: FC<StockKanbanSupplyContentFormBodyProps> = ({
    containerCode,
    container,
    content,
    containerCapacityMax,
    initialLot,
    initialQty,
    initialSpCode,
    readonly,
    createContentMutation,
    updateContentMutation,
    removeContentMutation,
}) => {
    const windowControls = useWindowControls();

    const formContext = useMemo(
        (): StockKanbanSupplyContentFormContext => ({
            containerCode,
            contentCode: content?.Code,
            containerCapacityMax,
        }),
        [containerCapacityMax, containerCode, content?.Code],
    );

    const boundFormConfig = useMemo(() => stockKanbanSupplyContentFormConfig.withContext(formContext), [formContext]);

    const initialValues = useMemo((): StockKanbanSupplyContentFormValues => {
        const resolvedSpCode = content != null ? content.SpCode : (initialSpCode ?? null);

        return {
            lot: initialLot,
            qty: content?.Qty ?? initialQty,
            spCode: resolvedSpCode,
        };
    }, [content, initialLot, initialQty, initialSpCode]);

    const handleSubmit = async (values: StockKanbanSupplyContentFormValues): Promise<void> => {
        if (readonly) {
            return;
        }

        if (!stockKanbanContainersServices.canFinishComplect(container)) {
            notification.warning("Менять содержимое можно только у контейнера на комплектовании.");
            return;
        }

        if (values.lot == null) {
            notification.warning("Выберите партию.");
            return;
        }

        // `spCode` обязателен через form-config; сюда с `null` не должны попасть.
        if (values.spCode == null) {
            throw new Error("Выберите ячейку хранения партии.");
        }

        const maxQty = resolveStockKanbanSupplyContentMaxQty(values.lot, values.spCode, containerCapacityMax);

        if (maxQty != null && values.qty > maxQty) {
            notification.warning(`Количество больше допустимого максимума (${maxQty}).`);
            return;
        }

        const payload = {
            containerCode,
            lotCode: values.lot.LotCode,
            qty: values.qty,
            spCode: values.spCode,
        };

        if (content == null) {
            await createContentMutation.mutateAsync(payload);
            return;
        }

        await updateContentMutation.mutateAsync({
            ...payload,
            contentCode: content.Code,
        });
    };

    const handleRemove = async (): Promise<void> => {
        if (content == null || readonly) {
            return;
        }

        if (!stockKanbanContainersServices.canFinishComplect(container)) {
            notification.warning("Удалять содержимое можно только у контейнера на комплектовании.");
            return;
        }

        const accepted = await confirm.show({
            title: "Удалить партию?",
            message: `Партия №${content.Lot?.LotNum ?? content.LotCode} будет удалена из контейнера.`,
            confirmText: "Удалить",
            cancelText: "Отмена",
        });

        if (!accepted) {
            return;
        }

        await removeContentMutation.mutateAsync({ contentCode: content.Code });
        windowControls.close();
    };

    const canRemove = content != null && !readonly;

    return (
        <FormPanel<StockKanbanSupplyContentFormValues>
            disabled={readonly}
            useDefaultFooter={false}
            formProps={{
                formConfig: boundFormConfig,
                initialValues,
                ready: true,
                onSubmit: handleSubmit,
            }}
            footer={
                !readonly
                    ? ({ submitForm, isFormSubmitting }) => (
                          <FormFooter
                              confirmText="Сохранить"
                              confirmLoading={isFormSubmitting}
                              confirmDisabled={removeContentMutation.isPending}
                              onConfirm={submitForm}
                              leadingAction={
                                  canRemove ? (
                                      <Button
                                          variant="danger"
                                          fullWidth
                                          loading={removeContentMutation.isPending}
                                          disabled={isFormSubmitting || removeContentMutation.isPending}
                                          onClick={() => {
                                              void handleRemove();
                                          }}
                                      >
                                          Удалить
                                      </Button>
                                  ) : undefined
                              }
                          />
                      )
                    : undefined
            }
        >
            {(form) => (
                <>
                    <BusinessForm.Field name="lot">
                        <StockKanbanSupplyLotField form={form} container={container} />
                    </BusinessForm.Field>
                    <StockKanbanSupplyLotCellField form={form} />
                    <BusinessForm.Field name="qty">
                        <Input
                            type="number"
                            endDecorator={measureEndDecorator(container.Measure)}
                            {...form.field("qty").getNumberInputProps()}
                        />
                    </BusinessForm.Field>
                </>
            )}
        </FormPanel>
    );
};

/** Ячейка по умолчанию для выбранной партии: «на полу» / единственная / ручной выбор (multi). */
function resolveInitialSpCodeForCells(cells: StorePlaceRemainder[] | null): number | null {
    if (cells == null || cells.length === 0) {
        return 0;
    }
    if (cells.length === 1) {
        return cells[0].SpCode;
    }
    return null;
}

interface StockKanbanSupplyLotFieldProps {
    form: FormPanelRenderProps<StockKanbanSupplyContentFormValues>;
    container: StockKanbanContainer;
}

/**
 * Поле выбора партии: опция уже несёт остатки по ячейкам,
 * поэтому при выборе синхронно выставляем `spCode` по режиму размещения (floor / single / multi).
 */
const StockKanbanSupplyLotField: FC<StockKanbanSupplyLotFieldProps> = ({ form, container }) => {
    const selectProps = form.field("lot").getSearchSelectProps<StockKanbanSupplyAvailableLotOption>((lot) => lot);

    return (
        <StockKanbanSupplyAvailableLotSearchSelect
            {...selectProps}
            container={container}
            onChange={(lot) => {
                selectProps.onChange(lot);
                form.setValue("spCode", lot == null ? null : resolveInitialSpCodeForCells(lot.availability.cells), {
                    shouldDirty: true,
                    shouldValidate: true,
                });
            }}
            fullscreenTitle="Выберите партию"
        />
    );
};

interface StockKanbanSupplyLotCellFieldProps {
    form: FormPanelRenderProps<StockKanbanSupplyContentFormValues>;
}

/**
 * Зависимое поле ячейки: читает остатки партии из выбранной опции (`values.lot`)
 * и рендерит floor / single / multi режимы. Без собственных запросов и sync-эффектов.
 */
const StockKanbanSupplyLotCellField: FC<StockKanbanSupplyLotCellFieldProps> = ({ form }) => {
    const lot = form.watch("lot");
    const spCode = form.watch("spCode");
    const spCodeUi = form.field("spCode").getUiProps();
    const fieldDisabled = form.disabled;

    const cells = lot?.availability.cells ?? null;
    const mode = resolveStockKanbanSupplyLotPlacementMode(lot);

    if (mode === "idle") {
        return null;
    }

    if (mode === "floor") {
        const floorAvailableQty = lot == null ? null : Math.max(lot.availability.lotQtyRest, 0);
        return (
            <DetailList layout="tiles" columns="auto" minWidth={96} oneLine>
                <DetailList.Row label="Ячейка хранения" value="Не обязательна — партия без размещения в ячейке (на полу)" />
                <DetailList.Row label="Доступно в партии" value={floorAvailableQty} />
            </DetailList>
        );
    }

    if (mode === "single") {
        const cell = cells?.[0];
        // Лимит для single — доступный остаток партии (`lotQtyRest`), а не остаток ячейки
        // из `loadRemaindersByFilter`; показываем то же значение, что и в правиле `max`.
        const singleAvailableQty = lot == null ? null : Math.max(lot.availability.lotQtyRest, 0);
        return (
            <DetailList layout="tiles" columns="auto" minWidth={96} oneLine>
                <DetailList.Row label="Ячейка хранения" value={formatKanbanSupplyCellName(cell)} />
                <DetailList.Row label="Доступно в партии" value={singleAvailableQty} />
            </DetailList>
        );
    }

    return (
        <BusinessForm.Field name="spCode">
            <BusinessSelect<StorePlaceRemainder, number>
                queryKey={["stock", "kanbanSupply", "lotCells", lot?.LotCode ?? null]}
                loadOptions={async () => cells ?? []}
                getOptionData={(remainder) => ({
                    value: remainder.SpCode,
                    label: formatKanbanSupplyCellOptionLabel(remainder),
                })}
                value={spCode}
                onChange={(_entity, rawValue) => {
                    if (fieldDisabled) {
                        return;
                    }
                    form.setValue("spCode", rawValue, { shouldDirty: true, shouldValidate: true });
                }}
                label={spCodeUi.label}
                required={spCodeUi.required}
                disabled={fieldDisabled}
                error={form.getFieldState("spCode").error != null}
                optionsLoadEnabled={lot != null}
                fullscreenTitle="Выберите ячейку хранения"
                placeholder="Выберите ячейку"
                optionSlots={{
                    fullscreen: (remainder) => (
                        <PickerOptionCard
                            title={formatKanbanSupplyCellName(remainder)}
                            details={[{ label: "Остаток партии", value: remainder.Qty }]}
                        />
                    ),
                }}
            />
        </BusinessForm.Field>
    );
};

/** Обозначение ячейки без остатка. */
function formatKanbanSupplyCellName(cell: StorePlaceRemainder | undefined): string {
    const formattedName = formatStorePlaceCellName(cell?.StorePlace);
    if (formattedName !== "") {
        return formattedName;
    }
    return cell == null ? "Ячейка" : `Ячейка ${cell.SpCode}`;
}

/** Подпись опции ячейки с остатком партии в этой ячейке. */
function formatKanbanSupplyCellOptionLabel(cell: StorePlaceRemainder | undefined): string {
    return `${formatKanbanSupplyCellName(cell)} (ост. ${cell?.Qty ?? 0})`;
}
